import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  Container,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Avatar,
  Stack,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Fade,
  Slide,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Email,
  Lock,
  Badge,
  LinkedIn,
  GitHub,
  Language,
  VpnKey,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AppContext';
import CollegeService from '../../services/collegeService';
import AuthService from '../../services/authService';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import AvatarSelector from '../../components/common/AvatarSelector';
import ParticleBackground from '../../components/common/ParticleBackground';
import { SignUpForm, College } from '../../types';
import { VALIDATION_RULES, USER_ROLES } from '../../utils/config';

// Form values type to match validation schema
interface RegisterFormValues {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: 'Student' | 'Faculty' | 'Ambassador';
  collegeId: string;
  branch: string;
  mobileNumber: string;
  rollNumber?: string;
  linkedin?: string;
  github?: string;
  website?: string;
}

// Validation schema
const validationSchema: yup.ObjectSchema<RegisterFormValues> = yup.object({
  name: yup
    .string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name cannot exceed 100 characters'),
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
  role: yup
    .mixed<'Student' | 'Faculty' | 'Ambassador'>()
    .oneOf(['Student', 'Faculty', 'Ambassador'])
    .required('Please select your role'),
  collegeId: yup
    .string()
    .required('Please select your college'),
  branch: yup
    .string()
    .required('Please select your branch'),
  mobileNumber: yup
    .string()
    .required('Mobile number is required')
    .matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit mobile number'),
  rollNumber: yup
    .string()
    .optional(),
  linkedin: yup
    .string()
    .optional()
    .test('linkedin-url', 'Please enter a valid LinkedIn URL', (value) => {
      if (!value) return true;
      return /^(https?:\/\/)?(www\.)?linkedin\.com\/.*$/.test(value);
    }),
  github: yup
    .string()
    .optional()
    .test('github-url', 'Please enter a valid GitHub URL', (value) => {
      if (!value) return true;
      return /^(https?:\/\/)?(www\.)?github\.com\/.*$/.test(value);
    }),
  website: yup
    .string()
    .optional()
    .test('website-url', 'Please enter a valid website URL', (value) => {
      if (!value) return true;
      try {
        new URL(value.startsWith('http') ? value : `https://${value}`);
        return true;
      } catch {
        return false;
      }
    }),
}) as yup.ObjectSchema<RegisterFormValues>;

const steps = ['College Selection', 'Email & Password', 'Personal Information'];

const RegisterPage: React.FC = () => {
  const { register: registerUser, loading, error: authError } = useAuth();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [colleges, setColleges] = useState<College[]>([]);
  const [loadingColleges, setLoadingColleges] = useState(true);
  const [selectedCollege, setSelectedCollege] = useState<College | null>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<string>('👤');
  const [showCollegePin, setShowCollegePin] = useState(false);
  const [collegePin, setCollegePin] = useState('');
  const [collegePinError, setCollegePinError] = useState('');
  const [showCustomBranch, setShowCustomBranch] = useState(false);
  const [customBranch, setCustomBranch] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [verifiedPassword, setVerifiedPassword] = useState('');
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<'warning' | 'error'>('warning');

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useForm<RegisterFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'Student',
      collegeId: '',
      branch: '',
      mobileNumber: '',
      rollNumber: '',
      linkedin: '',
      github: '',
      website: '',
    },
  });

  const selectedRole = watch('role');
  const selectedCollegeId = watch('collegeId');


  // Fetch colleges on mount
  useEffect(() => {
    const fetchColleges = async () => {
      try {
        setLoadingColleges(true);
        const data = await CollegeService.getAllColleges();
        setColleges(data);
      } catch (error) {
        // Silent fail
      } finally {
        setLoadingColleges(false);
      }
    };

    fetchColleges();
  }, []);

  // Update selected college when collegeId changes
  useEffect(() => {
    if (selectedCollegeId) {
      if (selectedCollegeId === 'other') {
        setShowCollegePin(true);
        setSelectedCollege(null);
        setValue('branch', '');
      } else {
        setShowCollegePin(false);
        setCollegePin('');
        setCollegePinError('');
        const college = colleges.find(c => c.id === selectedCollegeId);
        setSelectedCollege(college || null);
        // Reset branch when college changes
        setValue('branch', '');
      }
    }
  }, [selectedCollegeId, colleges, setValue]);

  // Handle step navigation
  const handleNext = async () => {
    // Validate current step before proceeding
    if (activeStep === 0) {
      // Step 0: College and branch selection
      const collegeId = watch('collegeId');
      const branch = watch('branch');

      if (!collegeId) {
        return; // Form validation will show errors
      }

      if (showCollegePin && !collegePin.trim()) {
        setCollegePinError('College PIN is required');
        return;
      }

      if (!branch) {
        return; // Form validation will show errors
      }

      // Move to next step (Email & Password)
      setActiveStep(1);
    } else if (activeStep === 1) {
      // Step 1: Email & Password - just move to next step, no OTP verification yet
      // Move to Personal Information step
      setActiveStep(2);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onSubmit = async (data: RegisterFormValues) => {
    try {
      // Validate college PIN if "Other" is selected
      if (showCollegePin) {
        if (!collegePin || collegePin.trim().length === 0) {
          setCollegePinError('College PIN is required');
          return;
        }
        // Here you would validate the PIN with your backend
        // For now, we'll just check if it's not empty
      }

      // Step 1: For Students only - Register with Firebase and send verification email
      // FIREBASE EMAIL VERIFICATION TEMPORARILY DISABLED
      // if (data.role === 'Student') {
      //   try {
      //     console.log('Creating Firebase user and sending verification email...');
      //     await EmailVerificationService.registerAndSendVerification(
      //       data.email,
      //       data.password
      //     );
      //     console.log('Firebase user created successfully');
      //   } catch (firebaseError: any) {
      //     // Handle Firebase-specific errors
      //     console.error('Firebase error:', firebaseError);
      //     throw new Error(firebaseError.message || 'Failed to send verification email');
      //   }
      // }

      // Step 2: Register with backend
      const formData: SignUpForm = {
        ...data,
        profilePicture: selectedAvatar,
      };

      // Register with backend - call AuthService directly to control navigation
      await AuthService.register(formData);

      // Navigate to OTP verification page with email and fromRegistration flag
      navigate('/verify-otp', {
        state: {
          email: data.email,
          fromRegistration: true
        }
      });
    } catch (error: any) {
      console.error('Registration error:', error);

      // Get error message from different possible locations
      const errorMessage = error.response?.data?.message ||
                          error.message ||
                          error.data?.message ||
                          '';

      // Check if user already exists
      if (errorMessage.toLowerCase().includes('already exists') ||
          errorMessage.toLowerCase().includes('already registered') ||
          errorMessage.toLowerCase().includes('email already in use')) {
        // Show warning alert for existing user
        setAlertType('warning');
        setAlertMessage('An account with this email already exists. Please login instead or use a different email.');
        // Scroll to top to show alert
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return; // Don't throw error, just show alert
      }

      // For other errors, show error alert with the error message
      if (errorMessage) {
        setAlertType('error');
        setAlertMessage(errorMessage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      <ParticleBackground />
      <Container component="main" maxWidth="md">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 4,
          }}
        >
        <Slide direction="down" in={true} timeout={600}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 3,
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              border: '1px solid',
              borderColor: 'rgba(0, 0, 0, 0.06)',
            }}
          >
            {/* Header */}
            <Fade in={true} timeout={800}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  Unii Club
                </Typography>
                <Typography
                  variant="h5"
                  color="text.primary"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Join Your College Community
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Connect with peers and grow together
                </Typography>
              </Box>
            </Fade>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

          {/* Alert message for errors */}
          {alertMessage && (
            <Alert
              severity={alertType}
              sx={{ mb: 3 }}
              onClose={() => {
                setAlertMessage(null);
                setAlertType('warning');
              }}
            >
              {alertMessage}
            </Alert>
          )}

          {/* Error message */}
          {authError && !alertMessage && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {authError}
            </Alert>
          )}

          {/* Register form */}
          <Box component="form" noValidate>
            {/* Step 0: College & Branch Selection */}
            {activeStep === 0 && (
              <Fade in={true} timeout={500}>
                <Stack spacing={3}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Select Your College & Branch
                  </Typography>

                  {/* College Dropdown */}
                  <Controller
                    name="collegeId"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.collegeId}>
                        <InputLabel id="college-select-label">Select Your College *</InputLabel>
                        <Select
                          value={field.value || ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          labelId="college-select-label"
                          id="college-select"
                          label="Select Your College *"
                          disabled={loadingColleges}
                        >
                          {loadingColleges ? (
                            <MenuItem disabled>Loading colleges...</MenuItem>
                          ) : colleges.length === 0 ? (
                            <MenuItem disabled>No colleges available</MenuItem>
                          ) : (
                            colleges.map((college) => (
                              <MenuItem key={college.id} value={college.id}>
                                {college.name}
                              </MenuItem>
                            )).concat(
                              <MenuItem key="other" value="other">
                                Other (Enter College PIN)
                              </MenuItem>
                            )
                          )}
                        </Select>
                        {errors.collegeId && (
                          <FormHelperText>{errors.collegeId.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />

                  {/* College Registration Note */}
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Important:</strong>
                    </Typography>
                    <Typography variant="body2" component="ul" sx={{ mb: 0, pl: 2 }}>
                      <li>If your college is listed, select it directly from the dropdown</li>
                      <li>If your college is not listed, email <strong>devashish@unii.club</strong> to request addition of your college</li>
                      <li>For existing colleges not in the list, select "Other" and enter the College PIN provided by your administrator</li>
                    </Typography>
                  </Alert>

                  {/* College PIN Input - Shows when "Other" is selected */}
                  {showCollegePin && (
                    <Box>
                      <TextField
                        fullWidth
                        label="College PIN *"
                        value={collegePin}
                        onChange={(e) => {
                          setCollegePin(e.target.value);
                          setCollegePinError('');
                        }}
                        error={!!collegePinError}
                        helperText={collegePinError || 'Enter the PIN provided by your college administrator'}
                        placeholder="e.g., ABC-XYZ-2024"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <VpnKey color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Contact your college administrator to get a College PIN
                      </Alert>
                    </Box>
                  )}

                  {/* Branch Selection */}
                  {!showCustomBranch && (
                    <Controller
                      name="branch"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.branch}>
                          <InputLabel id="branch-select-label">Branch/Department *</InputLabel>
                          <Select
                            value={field.value || ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            labelId="branch-select-label"
                            id="branch-select"
                            label="Branch/Department *"
                            disabled={!selectedCollege && !showCollegePin}
                          >
                            {!selectedCollege && !showCollegePin ? (
                              <MenuItem disabled>Please select a college first</MenuItem>
                            ) : selectedCollege && selectedCollege.branches.length === 0 ? (
                              <MenuItem disabled>No branches available</MenuItem>
                            ) : showCollegePin ? (
                              [
                                'Computer Science',
                                'Mechanical Engineering',
                                'Electronics Engineering',
                                'Civil Engineering',
                                'Information Technology',
                                'Electrical Engineering',
                                'Business Administration',
                                'Commerce',
                                'Arts',
                                'Science'
                              ].map((branch) => (
                                <MenuItem key={branch} value={branch}>
                                  {branch}
                                </MenuItem>
                              ))
                            ) : (
                              selectedCollege?.branches.map((branch) => (
                                <MenuItem key={branch} value={branch}>
                                  {branch}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                          {errors.branch && (
                            <FormHelperText>{errors.branch.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  )}

                  {/* Checkbox for Custom Branch */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showCustomBranch}
                        onChange={(e) => {
                          setShowCustomBranch(e.target.checked);
                          if (e.target.checked) {
                            setValue('branch', '');
                            setCustomBranch('');
                          } else {
                            setCustomBranch('');
                          }
                        }}
                        disabled={!selectedCollege && !showCollegePin}
                      />
                    }
                    label="My branch is not listed above"
                  />

                  {/* Custom Branch Input */}
                  {showCustomBranch && (
                    <TextField
                      fullWidth
                      label="Enter Your Branch/Department *"
                      value={customBranch}
                      onChange={(e) => {
                        setCustomBranch(e.target.value);
                        setValue('branch', e.target.value);
                      }}
                      error={!!errors.branch}
                      helperText={errors.branch?.message || 'Enter your branch or department name'}
                      placeholder="e.g., Biotechnology, Fashion Design, etc."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Badge color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}

                  {/* Navigation Button for Step 1 */}
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleNext}
                    disabled={!watch('collegeId') || !watch('branch')}
                    sx={{
                      mt: 2,
                      py: 1.75,
                      fontWeight: 600,
                      borderRadius: 2.5,
                      fontSize: '1.1rem',
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                        transform: 'translateY(-2px)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                  >
                    Next: Email & Password
                  </Button>
                </Stack>
              </Fade>
            )}

            {/* Step 1: Email & Password */}
            {activeStep === 1 && (
              <Fade in={true} timeout={500}>
                <Stack spacing={3}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Email & Password
                  </Typography>

                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Verification Process:</strong>
                    </Typography>
                    <Typography variant="body2" component="ul" sx={{ mb: 0, pl: 2, mt: 1 }}>
                      <li><strong>After Registration:</strong> You'll verify your email with OTP</li>
                      <li><strong>Management Approval:</strong> College management will review and approve your account</li>
                    </Typography>
                  </Alert>

                  <TextField
                    {...register('email')}
                    fullWidth
                    label="Email Address *"
                    type="email"
                    autoComplete="email"
                    error={!!errors.email}
                    helperText={errors.email?.message || 'Enter a valid email address'}
                    placeholder="your.email@example.com"
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    {...register('password')}
                    fullWidth
                    label="Password *"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    error={!!errors.password}
                    helperText={errors.password?.message || 'Minimum 6 characters'}
                    placeholder="Enter your password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    {...register('confirmPassword')}
                    fullWidth
                    label="Confirm Password *"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message || 'Re-enter your password'}
                    placeholder="Confirm your password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      onClick={handleBack}
                      sx={{
                        py: 1.75,
                        fontWeight: 600,
                        borderRadius: 2.5,
                        fontSize: '1.1rem',
                        textTransform: 'none',
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                        },
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handleNext}
                      disabled={!watch('email') || !watch('password') || !watch('confirmPassword')}
                      sx={{
                        py: 1.75,
                        fontWeight: 600,
                        borderRadius: 2.5,
                        fontSize: '1.1rem',
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                          boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                          transform: 'translateY(-2px)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                      }}
                    >
                      Next: Personal Information
                    </Button>
                  </Box>
                </Stack>
              </Fade>
            )}

            {/* Step 2: Personal Information */}
            {activeStep === 2 && (
              <Fade in={true} timeout={500}>
                <Box>
                <Stack spacing={3}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Personal Information
                  </Typography>

                  {/* Profile Avatar Selection */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          fontSize: '2rem',
                          bgcolor: 'primary.main'
                        }}
                      >
                        {selectedAvatar}
                      </Avatar>
                      <Typography variant="body2" color="text.secondary">
                        Your Selected Avatar
                      </Typography>
                    </Box>
                    <AvatarSelector
                      selectedAvatar={selectedAvatar}
                      onSelect={(avatar) => setSelectedAvatar(avatar)}
                    />
                  </Box>

                  {/* Name */}
                  <TextField
                    {...register('name')}
                    fullWidth
                    label="Full Name *"
                    autoComplete="name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Email - Read Only (from Step 1) */}
                  <TextField
                    {...register('email')}
                    fullWidth
                    label="Email Address *"
                    type="email"
                    autoComplete="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Role & Mobile Number */}
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Controller
                      name="role"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.role}>
                          <InputLabel id="role-select-label">Role *</InputLabel>
                          <Select
                            value={field.value || ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            labelId="role-select-label"
                            id="role-select"
                            label="Role *"
                          >
                            <MenuItem value="Student">Student</MenuItem>
                            <MenuItem value="Faculty">Faculty</MenuItem>
                            <MenuItem value="Ambassador">Ambassador</MenuItem>
                          </Select>
                          {errors.role && (
                            <FormHelperText>{errors.role.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />

                    <TextField
                      {...register('mobileNumber')}
                      fullWidth
                      label="Mobile Number *"
                      placeholder="10-digit mobile number"
                      error={!!errors.mobileNumber}
                      helperText={errors.mobileNumber?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Badge color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Roll Number */}
                  <TextField
                    {...register('rollNumber')}
                    fullWidth
                    label="Roll Number (Optional)"
                    error={!!errors.rollNumber}
                    helperText={errors.rollNumber?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Badge color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Social Links - Optional */}
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Optional: Social Links
                    </Typography>
                  </Divider>

                  {/* LinkedIn */}
                  <TextField
                    {...register('linkedin')}
                    fullWidth
                    label="LinkedIn Profile"
                    placeholder="https://linkedin.com/in/yourprofile"
                    error={!!errors.linkedin}
                    helperText={errors.linkedin?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkedIn color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* GitHub & Website */}
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      {...register('github')}
                      fullWidth
                      label="GitHub Profile"
                      placeholder="https://github.com/yourusername"
                      error={!!errors.github}
                      helperText={errors.github?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <GitHub color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      {...register('website')}
                      fullWidth
                      label="Website"
                      placeholder="https://yourwebsite.com"
                      error={!!errors.website}
                      helperText={errors.website?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Language color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Navigation Buttons for Step 2 */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      onClick={handleBack}
                      sx={{
                        py: 1.75,
                        fontWeight: 600,
                        borderRadius: 2.5,
                        fontSize: '1.1rem',
                        textTransform: 'none',
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                        },
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handleSubmit(onSubmit)}
                      disabled={isSubmitting || loading}
                      sx={{
                        py: 1.75,
                        fontWeight: 600,
                        borderRadius: 2.5,
                        fontSize: '1.1rem',
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                          boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                          transform: 'translateY(-2px)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                      }}
                    >
                      {isSubmitting || loading ? <LoadingSpinner size={24} message="" /> : 'Create Account'}
                    </Button>
                  </Box>
                </Stack>
              </Box>
              </Fade>
            )}

            {/* Information about verification process */}
            <Alert severity="info" sx={{ mt: 3, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>After Registration:</strong>
              </Typography>
              <Typography variant="body2" component="ul" sx={{ mb: 0, pl: 2 }}>
                <li><strong>Email Verification:</strong> You've verified your email with OTP ✓</li>
                <li><strong>Management Approval:</strong> Your account will be reviewed by college management (typically 1-2 business days)</li>
                <li>You'll receive email notification once your account is approved</li>
              </Typography>
            </Alert>

            {/* Switch to login */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate('/login')}
                  sx={{
                    textDecoration: 'none',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  Sign in here
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
        </Slide>
      </Box>
    </Container>
    </>
  );
};

export default RegisterPage;
